import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useApiClient from '../api/useApiClient';
import usePlatformAgentState from '../context/usePlatformAgentState';
import { useGetPlatformAgentUnreadQuery } from '../../store/api-slices';
import { platformAgentUnreadCount } from '../../services/platform-agent/platformAgent';
import { platformAgentBearer, platformAgentHeaders } from '../../services/platform-agent/platformAgentConversation';

// While the panel is closed, the button asks the agent for news once a minute.
export const PLATFORM_AGENT_UNREAD_POLLING_MS = 60_000;

/**
 * The Ask Amigo button's news (docs/platform-agent/contracts.md §6, GET /chats/unread): how many of the agent's answers
 * on this restaurant the person hasn't seen, e.g. an import's result that came in after the panel closed. Asked every
 * minute while the button shows; never while the panel is open, without a login, while the agent is down, or once the
 * button is gone. Another restaurant gets its own count, never this one's.
 * - `unread`: the count on the button and in its name. PlatformAgent mounts the button whenever the panel closes, and
 *   an answer from before then isn't shown: the panel just showed those answers. The panel marks the chats the person
 *   saw read (the chat it opened on when this news listed it, a chat picked from Chats, a chat an answer came in on),
 *   which asks the agent again, so the count after closing leaves those out; any other chat's news shows with it.
 * - `attention`: one more each time the count goes up, so the button sends its beam round once.
 */
export default function usePlatformAgentUnread() {
  const agentUrl = import.meta.env.VITE_PLATFORM_AGENT_URL;
  const { i18n } = useTranslation();
  const { PlatformAgentChatsManager } = useApiClient({ baseUrl: agentUrl, autoLogOut: false });
  const { available, unavailable, subjectKey, requestHeaders } = usePlatformAgentState();
  const open = useSelector((state) => state.platformAgent.open);

  // The person and the restaurant as the panel's chat sends them to the agent (contracts §1).
  const headers = useMemo(() => platformAgentHeaders(requestHeaders, i18n.language), [requestHeaders, i18n.language]);
  const loggedIn = platformAgentBearer(requestHeaders) !== null;

  const { currentData, fulfilledTimeStamp } = useGetPlatformAgentUnreadQuery(
    { apiManager: PlatformAgentChatsManager, subject: subjectKey, headers },
    {
      skip: !agentUrl || !loggedIn || !available || unavailable || open || !subjectKey,
      pollingInterval: PLATFORM_AGENT_UNREAD_POLLING_MS,
    },
  );

  // When the button appeared: the page's load, or the panel closing.
  const [shownSince] = useState(() => Date.now());
  const unread = fulfilledTimeStamp >= shownSince ? platformAgentUnreadCount(currentData) : 0;

  // The agent's last count on this restaurant: the beam goes round only when it rises, never on the first answer.
  const last = useRef(null);
  const [attention, setAttention] = useState(0);

  useEffect(() => {
    if (currentData === undefined) {
      return;
    }

    const count = platformAgentUnreadCount(currentData);
    const previous = last.current;
    last.current = { subject: subjectKey, count };

    if (previous?.subject === subjectKey && count > previous.count) {
      setAttention((runs) => runs + 1);
    }
  }, [currentData, subjectKey]);

  return { unread, attention };
}
